/*eslint no-useless-catch: "off" */
import type {RequestProviderFn} from '$types/request-response';

const safeGetJson = async (response: Response): Promise<Record<string, unknown>> => {
  /**
   * fetch spec indicates that 204 responses should throw if response.json() is called.
   *
   * 204 indicates no content returned, so don't bother getting the json response
   */
  if (response.status === 204) {
    return null;
  }

  if (response.json) {
    try {
      const json = await response.json();

      return json;
    } catch (error) {
      throw error;
    }
  }
};

const jsonFetchProvider: RequestProviderFn = async ({options, url}) => {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      if (response.json) {
        const jsonError = await response.json();
        throw jsonError;
      } else {
        throw response;
      }
    }

    const body = await safeGetJson(response);

    return body;
  } catch (error) {
    throw error;
  }
};

const parseHeaders = (headers: Headers) => {
  const headersIterator = Object(headers).entries();
  const result = [...headersIterator].reduce(
    (headerMap, [key, value]) => ({
      ...headerMap,
      [key]: value,
    }),
    {},
  );

  return result;
};

const headParserProvider: RequestProviderFn = async ({options, url}) => {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      if (response.json) {
        const jsonError = await response.json();
        throw jsonError;
      } else {
        throw response;
      }
    }

    const body = parseHeaders(response.headers);

    return body;
  } catch (error) {
    throw error;
  }
};

const blobProvider: RequestProviderFn = async ({options, url}) => {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      if (response.json) {
        const jsonError = await response.json();
        throw jsonError;
      } else {
        throw response;
      }
    }

    const blob = await response.blob();
    const headersMap = parseHeaders(response.headers);
    const fileUrl = window.URL.createObjectURL(blob);

    return {...headersMap, url: fileUrl};
  } catch (error) {
    throw error;
  }
};

export {blobProvider, headParserProvider, jsonFetchProvider};
